import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  InputAdornment,
  Alert,
  Chip,
  Stack,
  IconButton,
  useTheme,
  alpha,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Close as CloseIcon,
  AccountBalanceWallet as WalletIcon,
} from '@mui/icons-material';

const AdvancePaymentSelectorDialog = ({
  open,
  onClose,
  customerId,
  customerName,
  advanceBalance,
  billTotal,
  onUseAdvance,
  onSkip
}) => {
  const theme = useTheme();
  // const isDark = theme.palette.mode === 'dark';
  const [useAdvance, setUseAdvance] = useState(false);
  const [usageAmounts, setUsageAmounts] = useState({
    cash: '',
    online: '',
    cheque: '',
    oldGold: '',
    oldSilver: '',
    other: ''
  });

  // FIXED: Handle both possible data structures
  let availableBalances = {};
  let totalAvailable = 0;

  if (advanceBalance) {
    // Check if data is in byMode format or direct format
    if (advanceBalance.byMode) {
      // Format: { byMode: { cash: 100, online: 200 }, total: 300 }
      availableBalances = advanceBalance.byMode;
      totalAvailable = advanceBalance.total || 0;
    } else {
      // Format: { cash: 100, online: 200, total: 300 } - direct format
      availableBalances = {
        cash: advanceBalance.cash || 0,
        online: advanceBalance.online || 0,
        cheque: advanceBalance.cheque || 0,
        oldGold: advanceBalance.oldGold || 0,
        oldSilver: advanceBalance.oldSilver || 0,
        other: advanceBalance.other || 0
      };
      totalAvailable = advanceBalance.total || Object.values(availableBalances).reduce((sum, val) => sum + val, 0);
    }
  }

  // Also ensure all modes exist with default 0
  const safeBalances = {
    cash: availableBalances.cash || 0,
    online: availableBalances.online || 0,
    cheque: availableBalances.cheque || 0,
    oldGold: availableBalances.oldGold || 0,
    oldSilver: availableBalances.oldSilver || 0,
    other: availableBalances.other || 0
  };

  useEffect(() => {
    // Reset when dialog opens
    if (open) {
      setUseAdvance(false);
      setUsageAmounts({
        cash: '', online: '', cheque: '', oldGold: '', oldSilver: '', other: ''
      });
    }
  }, [open]);

  const calculateTotalUsing = () => {
    return Object.values(usageAmounts).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
  };

  const handleUsageChange = (mode, value) => {
    const amount = parseFloat(value) || 0;
    const maxAvailable = safeBalances[mode] || 0;

    if (amount > maxAvailable) {
      alert(`Cannot use more than available advance of ₹${maxAvailable.toFixed(2)} for ${mode}`);
      return;
    }

    setUsageAmounts(prev => ({ ...prev, [mode]: value }));
  };

  const handleUseAdvance = () => {
    if (!useAdvance) {
      if (onSkip) onSkip();
      onClose();
      return;
    }

    const totalToUse = calculateTotalUsing();
    if (totalToUse <= 0) {
      alert('Please enter amount to use from advance');
      return;
    }

    if (totalToUse > billTotal) {
      alert(`Cannot use advance more than bill total of ₹${billTotal.toFixed(2)}`);
      return;
    }

    // Filter out modes with zero usage
    const filteredUsageAmounts = {};
    Object.entries(usageAmounts).forEach(([mode, amount]) => {
      const numAmount = parseFloat(amount);
      if (numAmount > 0) {
        filteredUsageAmounts[mode] = numAmount;
      }
    });

    console.log("Calling onUseAdvance with:", {
      usageAmounts: filteredUsageAmounts,
      totalAmount: totalToUse
    });

    // Call the parent callback
    if (onUseAdvance) {
      onUseAdvance({
        usageAmounts: filteredUsageAmounts,
        totalAmount: totalToUse
      });
    }

    onClose();
  };

  const totalToUse = calculateTotalUsing();

  // Check if any balance exists
  const hasAnyBalance = Object.values(safeBalances).some(val => val > 0);

  if (!advanceBalance || !hasAnyBalance) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <WalletIcon color="primary" />
            <Typography variant="h6" fontWeight={700}>
              Available Advance Payment
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          Customer: {customerName}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          {/* Available Balance Summary - Shows ALL modes with positive balance */}
          <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.success.main, 0.1), borderRadius: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1" fontWeight={600}>
                Available Advance Balance
              </Typography>
              <Chip
                label={`Total: ₹${totalAvailable.toFixed(2)}`}
                color="success"
                sx={{ fontWeight: 600 }}
              />
            </Box>

            <Grid container spacing={1} sx={{ mt: 1 }}>
              {safeBalances.cash > 0 && (
                <Grid item xs={6} sm={4}>
                  <Chip
                    label={`Cash: ₹${safeBalances.cash.toFixed(2)}`}
                    size="small"
                    variant="outlined"
                    sx={{ width: '100%' }}
                  />
                </Grid>
              )}
              {safeBalances.online > 0 && (
                <Grid item xs={6} sm={4}>
                  <Chip
                    label={`Online: ₹${safeBalances.online.toFixed(2)}`}
                    size="small"
                    variant="outlined"
                    sx={{ width: '100%' }}
                  />
                </Grid>
              )}
              {safeBalances.cheque > 0 && (
                <Grid item xs={6} sm={4}>
                  <Chip
                    label={`Cheque: ₹${safeBalances.cheque.toFixed(2)}`}
                    size="small"
                    variant="outlined"
                    sx={{ width: '100%' }}
                  />
                </Grid>
              )}
              {safeBalances.oldGold > 0 && (
                <Grid item xs={6} sm={4}>
                  <Chip
                    label={`Old Gold: ₹${safeBalances.oldGold.toFixed(2)}`}
                    size="small"
                    variant="outlined"
                    sx={{ width: '100%' }}
                  />
                </Grid>
              )}
              {safeBalances.oldSilver > 0 && (
                <Grid item xs={6} sm={4}>
                  <Chip
                    label={`Old Silver: ₹${safeBalances.oldSilver.toFixed(2)}`}
                    size="small"
                    variant="outlined"
                    sx={{ width: '100%' }}
                  />
                </Grid>
              )}
              {safeBalances.other > 0 && (
                <Grid item xs={6} sm={4}>
                  <Chip
                    label={`Other: ₹${safeBalances.other.toFixed(2)}`}
                    size="small"
                    variant="outlined"
                    sx={{ width: '100%' }}
                  />
                </Grid>
              )}
            </Grid>
          </Paper>

          {/* Bill Total Display */}
          {billTotal > 0 && (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2">Current Bill Total:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  ₹{billTotal.toFixed(2)}
                </Typography>
              </Stack>
            </Alert>
          )}

          {/* Option to use advance */}
          <FormControlLabel
            control={
              <Checkbox
                checked={useAdvance}
                onChange={(e) => setUseAdvance(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography variant="subtitle1" fontWeight={600}>
                Use Advance Payment for this Bill
              </Typography>
            }
          />

          {useAdvance && (
            <Box sx={{ pl: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                You can use partial amounts from each payment mode.
                The used amounts will be deducted from available advance.
              </Alert>

              <Grid container spacing={2}>
                {safeBalances.cash > 0 && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={`Cash Advance`}
                      value={usageAmounts.cash}
                      onChange={(e) => handleUsageChange('cash', e.target.value)}
                      fullWidth
                      size="small"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        endAdornment: (
                          <InputAdornment position="end">
                            <Chip
                              label={`Max: ₹${safeBalances.cash.toFixed(2)}`}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.65rem' }}
                            />
                          </InputAdornment>
                        )
                      }}
                      helperText={`Available: ₹${safeBalances.cash.toFixed(2)}`}
                    />
                  </Grid>
                )}

                {safeBalances.online > 0 && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={`Online Advance`}
                      value={usageAmounts.online}
                      onChange={(e) => handleUsageChange('online', e.target.value)}
                      fullWidth
                      size="small"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        endAdornment: (
                          <InputAdornment position="end">
                            <Chip
                              label={`Max: ₹${safeBalances.online.toFixed(2)}`}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.65rem' }}
                            />
                          </InputAdornment>
                        )
                      }}
                      helperText={`Available: ₹${safeBalances.online.toFixed(2)}`}
                    />
                  </Grid>
                )}

                {safeBalances.cheque > 0 && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={`Cheque Advance`}
                      value={usageAmounts.cheque}
                      onChange={(e) => handleUsageChange('cheque', e.target.value)}
                      fullWidth
                      size="small"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        endAdornment: (
                          <InputAdornment position="end">
                            <Chip
                              label={`Max: ₹${safeBalances.cheque.toFixed(2)}`}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.65rem' }}
                            />
                          </InputAdornment>
                        )
                      }}
                      helperText={`Available: ₹${safeBalances.cheque.toFixed(2)}`}
                    />
                  </Grid>
                )}

                {safeBalances.oldGold > 0 && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={`Old Gold Advance`}
                      value={usageAmounts.oldGold}
                      onChange={(e) => handleUsageChange('oldGold', e.target.value)}
                      fullWidth
                      size="small"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        endAdornment: (
                          <InputAdornment position="end">
                            <Chip
                              label={`Max: ₹${safeBalances.oldGold.toFixed(2)}`}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.65rem' }}
                            />
                          </InputAdornment>
                        )
                      }}
                      helperText={`Available: ₹${safeBalances.oldGold.toFixed(2)}`}
                    />
                  </Grid>
                )}

                {safeBalances.oldSilver > 0 && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={`Old Silver Advance`}
                      value={usageAmounts.oldSilver}
                      onChange={(e) => handleUsageChange('oldSilver', e.target.value)}
                      fullWidth
                      size="small"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        endAdornment: (
                          <InputAdornment position="end">
                            <Chip
                              label={`Max: ₹${safeBalances.oldSilver.toFixed(2)}`}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.65rem' }}
                            />
                          </InputAdornment>
                        )
                      }}
                      helperText={`Available: ₹${safeBalances.oldSilver.toFixed(2)}`}
                    />
                  </Grid>
                )}
              </Grid>

              {totalToUse > 0 && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2">
                      Total advance to use: <strong>₹{totalToUse.toFixed(2)}</strong>
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Remaining after bill: <strong>₹{(totalAvailable - totalToUse).toFixed(2)}</strong>
                    </Typography>
                  </Box>
                </Alert>
              )}

              {totalToUse > billTotal && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  Total advance amount (₹{totalToUse.toFixed(2)}) exceeds bill total (₹{billTotal.toFixed(2)})
                </Alert>
              )}
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleUseAdvance}
          variant="contained"
          disabled={useAdvance && totalToUse <= 0}
          sx={{
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
            }
          }}
        >
          {useAdvance ? (totalToUse > 0 ? `Use Advance (₹${totalToUse.toFixed(2)})` : 'Enter Amount') : 'Skip Advance'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdvancePaymentSelectorDialog;